import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface AverageBlurParams {
  radius: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const AverageBlur: Filter<AverageBlurParams> = {
  id: "average-blur",
  displayName: "Average Blur",
  defaultParams: { radius: 5 },

  apply(input: PixelBuffer, params: AverageBlurParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0 || params.radius < 1) {
      dst.set(src);
      return out;
    }

    const r = Math.round(params.radius);
    const totalPixels = (2 * r + 1) * (2 * r + 1);

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sumR = 0, sumG = 0, sumB = 0, sumA = 0;
        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            const sx = Math.max(0, Math.min(w - 1, x + dx));
            const sy = Math.max(0, Math.min(h - 1, y + dy));
            const idx = (sy * w + sx) * 4;
            sumR += src[idx]!;
            sumG += src[idx + 1]!;
            sumB += src[idx + 2]!;
            sumA += src[idx + 3]!;
          }
        }
        const o = (y * w + x) * 4;
        dst[o] = clamp8(Math.round(sumR / totalPixels));
        dst[o + 1] = clamp8(Math.round(sumG / totalPixels));
        dst[o + 2] = clamp8(Math.round(sumB / totalPixels));
        dst[o + 3] = clamp8(Math.round(sumA / totalPixels));
      }
    }
    return out;
  },
};
